import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, Grid, Avatar, Chip, Button, IconButton,
  TextField, InputAdornment, Drawer, Divider, MenuItem,
  CircularProgress, Alert, Snackbar, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, LinearProgress, Switch,
  Card, CardContent, CardActions,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import {
  Search, Refresh, Close,
  Email, Phone, LinkedIn, Language, Work, LocationOn,
  CheckCircle, Cancel, Person,
  WhatsApp, CalendarMonth, Visibility,
  ArrowForward, EmojiPeople, AssignmentTurnedIn,
  HourglassTop, Groups, Inbox,
  CampaignOutlined, BusinessCenter, ToggleOn, ToggleOff,
  Add, Edit, Delete, PauseCircle, PlayCircle,
  EventAvailable, NotificationsActive, BookmarkBorder,
  Assignment, HomeOutlined, ArticleOutlined, QuestionAnswer,
} from "@mui/icons-material";
import api from "../../services/api";
import InterviewChecklist from "./InterviewChecklist";
import InterviewQuestionsGuide from "./InterviewQuestionsGuide";

// ── Paleta de estados ──────────────────────────────────────────────────────────
const ESTADO_META = {
  pendiente:      { label: "Pendiente",      color: "#f59e0b", bg: "rgba(245,158,11,0.1)",  icon: <HourglassTop sx={{ fontSize: 14 }} />, order: 0 },
  en_revision:    { label: "En revisión",    color: "#3b82f6", bg: "rgba(59,130,246,0.1)", icon: <Visibility sx={{ fontSize: 14 }} />,    order: 1 },
  en_entrevista:  { label: "En entrevista",  color: "#8b5cf6", bg: "rgba(139,92,246,0.1)", icon: <CalendarMonth sx={{ fontSize: 14 }} />, order: 2 },
  en_proceso:     { label: "En proceso",     color: "#06b6d4", bg: "rgba(6,182,212,0.1)",  icon: <Groups sx={{ fontSize: 14 }} />,        order: 3 },
  aprobada:       { label: "Aprobada",       color: "#22c55e", bg: "rgba(34,197,94,0.1)",   icon: <CheckCircle sx={{ fontSize: 14 }} />,   order: 4 },
  rechazada:      { label: "Rechazada",      color: "#ef4444", bg: "rgba(239,68,68,0.1)",   icon: <Cancel sx={{ fontSize: 14 }} />,         order: 5 },
};

const DISPONIBILIDAD_LABEL = {
  inmediata: "Inmediata 🟢",
  quincena:  "En 15 días 🟡",
  mes:       "En 1 mes 🟠",
  otro:      "Otro 🔵",
};



const EXP_LABEL = { 0: "Sin experiencia", 1: "1 año", 2: "2 años", 3: "3 años", 5: "5+ años" };

// ── Helpers ───────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const avatarColor = (name = "") => {
  const colors = ["#6366f1","#8b5cf6","#ec4899","#f43f5e","#f97316","#eab308","#22c55e","#14b8a6","#3b82f6","#06b6d4"];
  return colors[name.charCodeAt(0) % colors.length];
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ── KPI Card ─────────────────────────────────────────────────────────────────
const KPICard = ({ icon, label, value, color, sublabel }) => (
  <Box sx={{
    p: { xs: 1.5, sm: 2.5 }, borderRadius: "16px",
    height: "100%",
    background: `linear-gradient(135deg, ${color}18, ${color}08)`,
    border: `1px solid ${color}30`,
    display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 },
    transition: "all 0.2s", "&:hover": { transform: "translateY(-2px)", boxShadow: `0 8px 24px ${color}20` },
  }}>
    <Box sx={{ width: { xs: 38, sm: 48 }, height: { xs: 38, sm: 48 }, borderRadius: "12px", background: `${color}20`,
      display: "flex", alignItems: "center", justifyContent: "center", color, fontSize: { xs: 18, sm: 22 }, flexShrink: 0 }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1, fontSize: { xs: "1.15rem", sm: "1.5rem" } }}>{value}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", mt: 0.2, fontSize: { xs: "0.7rem", sm: "0.875rem" }, lineHeight: 1.2 }}>{label}</Typography>
      {sublabel && <Typography variant="caption" sx={{ color: "text.disabled", fontSize: { xs: "0.62rem", sm: "0.75rem" }, display: "block" }}>{sublabel}</Typography>}
    </Box>
  </Box>
);

// ── Chip de habilidad ──────────────────────────────────────────────────────────
const SkillChip = ({ label }) => (
  <Chip label={label} size="small" sx={{
    borderRadius: "8px", fontSize: "0.72rem", fontWeight: 600,
    background: "linear-gradient(90deg,rgba(99,102,241,0.12),rgba(139,92,246,0.12))",
    border: "1px solid rgba(99,102,241,0.2)", color: "#a5b4fc",
    height: 24,
  }} />
);

// ── Tarjeta de candidato ───────────────────────────────────────────────────────
const CandidatoCard = ({ post, onSelect, onQuickAction }) => {
  const meta = ESTADO_META[post.estado] ?? ESTADO_META.pendiente;
  const skills = (post.habilidades || "").split(",").map(s => s.trim()).filter(Boolean).slice(0, 4);

  return (
    <Box
      onClick={() => onSelect(post)}
      sx={{
        p: 2.5, borderRadius: "16px", cursor: "pointer",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        transition: "all 0.2s",
        "&:hover": {
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(99,102,241,0.3)",
          transform: "translateY(-2px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 1.5 }}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: avatarColor(post.nombre_completo), fontWeight: 700, fontSize: "0.9rem", flexShrink: 0 }}>
          {getInitials(post.nombre_completo)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {post.nombre_completo}
            </Typography>
            <Chip
              icon={meta.icon} label={meta.label} size="small"
              sx={{ borderRadius: "8px", fontSize: "0.67rem", fontWeight: 700,
                background: meta.bg, color: meta.color, border: `1px solid ${meta.color}30`,
                height: 22, flexShrink: 0, "& .MuiChip-icon": { color: meta.color } }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, mt: 0.3, flexWrap: "wrap" }}>
            {post.puesto_interes && (
              <Typography variant="caption" sx={{ color: "#a5b4fc", display: "flex", alignItems: "center", gap: 0.3 }}>
                <Work sx={{ fontSize: 12 }} />{post.puesto_interes}
              </Typography>
            )}
            {!!post.primera_experiencia && (
              <Chip
                label="1ª exp. laboral"
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.63rem",
                  fontWeight: 700,
                  background: "rgba(130,80,255,0.12)",
                  border: "1px solid rgba(130,80,255,0.35)",
                  color: "#c4a8ff",
                  borderRadius: "6px",
                  "& .MuiChip-label": { px: 0.8 },
                }}
              />
            )}
            {post.ciudad && (
              <Typography variant="caption" sx={{ color: "text.disabled", display: "flex", alignItems: "center", gap: 0.3 }}>
                <LocationOn sx={{ fontSize: 12 }} />{post.ciudad}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {skills.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mb: 1.5 }}>
          {skills.map(s => <SkillChip key={s} label={s} />)}
          {(post.habilidades || "").split(",").filter(Boolean).length > 4 && (
            <Chip label={`+${(post.habilidades || "").split(",").filter(Boolean).length - 4}`} size="small"
              sx={{ borderRadius: "8px", fontSize: "0.68rem", bgcolor: "rgba(255,255,255,0.06)", color: "text.secondary", height: 24 }} />
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          📅 {fmtDate(post.created_at)}
          {post.experiencia_anos > 0 && <span> · {EXP_LABEL[post.experiencia_anos] || `${post.experiencia_anos} años`} exp.</span>}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }} onClick={e => e.stopPropagation()}>
          <Tooltip title="WhatsApp">
            <IconButton size="small" sx={{ color: "#25D366", p: 0.7 }}
              onClick={() => post.telefono && window.open(`https://wa.me/${post.telefono.replace(/\D/g,'')}`, "_blank")}>
              <WhatsApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enviar email">
            <IconButton size="small" sx={{ color: "#60a5fa", p: 0.7 }}
              onClick={() => window.open(`mailto:${post.email}?subject=Tu postulación en A La Vuelta 360°`, "_blank")}>
              <Email sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver perfil completo">
            <IconButton size="small" sx={{ color: "text.secondary", p: 0.7 }}
              onClick={() => onSelect(post)}>
              <ArrowForward sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// ── Tarjeta de vacante ────────────────────────────────────────────────────────
const TIPO_META = {
  tiempo_completo: { label: "Tiempo completo", color: "#6366f1" },
  medio_tiempo:    { label: "Medio tiempo",    color: "#8b5cf6" },
  freelance:       { label: "Freelance",       color: "#ec4899" },
  por_evento:      { label: "Por evento",      color: "#f97316" },
};

// eslint-disable-next-line no-unused-vars
const VacanteCard = ({ v, onEdit, onDelete, onToggle }) => {
  const tipo = TIPO_META[v.tipo] ?? TIPO_META.por_evento;
  return (
    <Card sx={{ borderRadius: "16px", background: v.activo ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.2)",
      border: v.activo ? "1px solid rgba(255,255,255,0.1)" : "1px dashed rgba(255,255,255,0.08)",
      boxShadow: "none", transition: "all 0.2s",
      "&:hover": { border: `1px solid ${tipo.color}40`, boxShadow: `0 4px 20px ${tipo.color}15` } }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, color: v.activo ? "text.primary" : "text.disabled", lineHeight: 1.3 }}>
              {v.titulo}
            </Typography>
            {v.area && (
              <Typography variant="caption" sx={{ color: "text.disabled", display: "flex", alignItems: "center", gap: 0.3, mt: 0.3 }}>
                <BusinessCenter sx={{ fontSize: 12 }} />{v.area}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.8 }}>
            <Chip label={tipo.label} size="small" sx={{ borderRadius: "8px", fontSize: "0.67rem", fontWeight: 700, height: 22,
              background: `${tipo.color}18`, color: tipo.color, border: `1px solid ${tipo.color}30` }} />
            <Chip label={v.activo ? "Activa" : "Pausada"} size="small" sx={{ borderRadius: "8px", fontSize: "0.67rem", fontWeight: 700, height: 20,
              background: v.activo ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.05)",
              color: v.activo ? "#22c55e" : "text.disabled",
              border: `1px solid ${v.activo ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.08)"}` }} />
          </Box>
        </Box>
        {v.descripcion && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {v.descripcion}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, pt: 0.5, gap: 0.8, justifyContent: "flex-end" }}>
        <Tooltip title={v.activo ? "Pausar vacante" : "Activar vacante"}>
          <IconButton size="small" onClick={() => onToggle(v)} sx={{ color: v.activo ? "#f59e0b" : "#22c55e",
            background: v.activo ? "rgba(245,158,11,0.1)" : "rgba(34,197,94,0.1)", borderRadius: "8px", p: 0.8,
            "&:hover": { background: v.activo ? "rgba(245,158,11,0.2)" : "rgba(34,197,94,0.2)" } }}>
            {v.activo ? <ToggleOff sx={{ fontSize: 18 }} /> : <ToggleOn sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => onEdit(v)} sx={{ color: "#60a5fa",
            background: "rgba(96,165,250,0.1)", borderRadius: "8px", p: 0.8,
            "&:hover": { background: "rgba(96,165,250,0.2)" } }}>
            <Edit sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar">
          <IconButton size="small" onClick={() => onDelete(v)} sx={{ color: "#ef4444",
            background: "rgba(239,68,68,0.08)", borderRadius: "8px", p: 0.8,
            "&:hover": { background: "rgba(239,68,68,0.15)" } }}>
            <Delete sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

const VACANTE_INIT = { titulo: "", descripcion: "", area: "", tipo: "por_evento", requisitos: "", beneficios: "", activo: true, orden: 0 };

// ── Pipeline visual ───────────────────────────────────────────────────────────
const PipelineBar = ({ counts }) => {
  const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
  const stages = [
    { key: "pendiente",     color: "#f59e0b", label: "Pendiente" },
    { key: "en_revision",   color: "#3b82f6", label: "En revisión" },
    { key: "en_entrevista", color: "#8b5cf6", label: "En entrevista" },
    { key: "en_proceso",    color: "#06b6d4", label: "En proceso" },
    { key: "aprobada",      color: "#22c55e", label: "Aprobado" },
    { key: "rechazada",     color: "#ef4444", label: "Rechazado" },
  ];
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", gap: 0.5, borderRadius: "8px", overflow: "hidden", height: 10 }}>
        {stages.map(s => (
          <Tooltip key={s.key} title={`${s.label}: ${counts[s.key] ?? 0}`}>
            <Box sx={{ flex: (counts[s.key] ?? 0) / total, bgcolor: s.color, transition: "flex 0.5s", minWidth: counts[s.key] > 0 ? 4 : 0 }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, mt: 1, flexWrap: "wrap" }}>
        {stages.map(s => (
          <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: { xs: "calc(50% - 4px)", sm: "auto" } }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>{s.label} ({counts[s.key] ?? 0})</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Helpers de entrevistas ────────────────────────────────────────────────────
const TIPO_ENTREVISTA_META = {
  videollamada: { label: "Videollamada", color: "#3b82f6", emoji: "📹" },
  presencial:   { label: "Presencial",   color: "#22c55e", emoji: "🏢" },
  telefonica:   { label: "Telefónica",   color: "#f59e0b", emoji: "📞" },
};
const getFechaDate   = (fe) => (!fe ? null : fe.includes("T") ? fe.split("T")[0] : fe.split(" ")[0]);
const getHoraDisplay = (fe) => {
  if (!fe) return "—:—";
  const t = fe.includes("T") ? fe.split("T")[1] : (fe.split(" ")[1] || "");
  return t.slice(0, 5) || "—:—";
};

// ── Tarjeta de entrevista ─────────────────────────────────────────────────────
const InterviewCard = ({ item, isToday, isPast, onOpen }) => {
  const hora  = getHoraDisplay(item.fecha_entrevista);
  const tipo  = TIPO_ENTREVISTA_META[item.tipo_entrevista] ?? TIPO_ENTREVISTA_META.videollamada;
  const color = isPast ? "#6b7280" : isToday ? "#f59e0b" : "#8b5cf6";
  return (
    <Box onClick={() => onOpen(item)} sx={{
      display: "flex", alignItems: "center", gap: 1.5, p: "12px 14px",
      borderRadius: "14px", cursor: "pointer",
      background: isToday ? "rgba(245,158,11,0.07)" : isPast ? "rgba(239,68,68,0.04)" : "rgba(255,255,255,0.03)",
      border: `1px solid ${isToday ? "rgba(245,158,11,0.28)" : isPast ? "rgba(239,68,68,0.15)" : "rgba(255,255,255,0.07)"}`,
      transition: "all 0.18s",
      "&:hover": { transform: "translateY(-1px)", boxShadow: `0 4px 18px ${color}22` },
    }}>
      <Box sx={{ width: 50, textAlign: "center", flexShrink: 0 }}>
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, color, lineHeight: 1 }}>{hora}</Typography>
        <Typography sx={{ fontSize: "0.58rem", color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.5 }}>hrs</Typography>
      </Box>
      <Box sx={{ width: 2, height: 40, borderRadius: 1, bgcolor: color, opacity: 0.4, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.3, flexWrap: "wrap" }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: "0.6rem", fontWeight: 800, bgcolor: color + "33", color }}>
            {(item.nombre_completo || "").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase()}
          </Avatar>
          <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "rgba(255,255,255,0.9)" }} noWrap>{item.nombre_completo}</Typography>
          {isPast && <Chip label="Vencida" size="small" sx={{ height: 15, fontSize: "0.58rem", bgcolor: "rgba(239,68,68,0.15)", color: "#ef4444", "& .MuiChip-label": { px: 0.8 } }} />}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.72rem" }} noWrap>{item.puesto_interes || "Puesto no especificado"}</Typography>
          <Chip label={`${tipo.emoji} ${tipo.label}`} size="small"
            sx={{ height: 17, fontSize: "0.6rem", bgcolor: tipo.color + "22", color: tipo.color, "& .MuiChip-label": { px: 0.8 } }} />
          {item.lugar_entrevista && (
            <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.68rem", display: "flex", alignItems: "center", gap: 0.2 }} noWrap>
              <LocationOn sx={{ fontSize: 10 }} />
              {item.lugar_entrevista.length > 28 ? item.lugar_entrevista.slice(0, 28) + "…" : item.lugar_entrevista}
            </Typography>
          )}
        </Box>
      </Box>
      <ArrowForward sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }} />
    </Box>
  );
};

const InterviewGroup = ({ label, icon, items, isToday, isPast, onOpen }) =>
  items.length === 0 ? null : (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.68rem" }}>
          {icon} {label}
        </Typography>
        <Box sx={{ height: 1, flex: 1, bgcolor: "rgba(255,255,255,0.07)", ml: 0.5, borderRadius: 1 }} />
        <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.65rem", bgcolor: "rgba(255,255,255,0.05)", px: 1, py: 0.2, borderRadius: "6px" }}>
          {items.length}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {items.map(p => <InterviewCard key={p.id} item={p} isToday={isToday} isPast={isPast} onOpen={onOpen} />)}
      </Box>
    </Box>
  );

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
const AdminRHPanel = ({ activeSection }) => {
  // activeSection: undefined=modo autónomo | "overview" | "candidatos" | "puestos"
  const [tabRH, setTabRH] = useState(0); // 0=Candidatos, 1=Puestos (modo autónomo)

  // ── Campaña ────────────────────────────────────────────────────────────────
  const [campanaActiva, setCampanaActiva] = useState(true);
  const [loadingCampana, setLoadingCampana] = useState(true);
  const [savingCampana, setSavingCampana] = useState(false);

  // ── Candidatos ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [postulaciones, setPostulaciones] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [busqueda, setBusqueda] = useState("");
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [entrevistaOpen, setEntrevistaOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState({ open: false, accion: null, id: null });
  const [saving, setSaving] = useState(false);
  const [entrevistaForm, setEntrevistaForm] = useState({ fecha: "", hora: "10:00", tipo: "videollamada", lugar: "", notas: "" });
  const [rechazoOpen, setRechazoOpen] = useState(false);
  const [rechazoMotivo, setRechazoMotivo] = useState("");
  const [rechazoId, setRechazoId] = useState(null);
  const [busquedaReserva, setBusquedaReserva] = useState("");
  const [drawerTab, setDrawerTab] = useState(0);          // 0=Perfil | 1=Checklist | 2=Guía | 3=CV
  const [savingChecklist, setSavingChecklist] = useState(false);

  // ── Vacantes ───────────────────────────────────────────────────────────────
  const [vacantes, setVacantes] = useState([]);
  const [loadingVacantes, setLoadingVacantes] = useState(false);
  const [vacanteDialogOpen, setVacanteDialogOpen] = useState(false);
  const [vacanteForm, setVacanteForm] = useState(VACANTE_INIT);
  const [vacanteEditId, setVacanteEditId] = useState(null);
  const [savingVacante, setSavingVacante] = useState(false);
  const [confirmDeleteVacante, setConfirmDeleteVacante] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, msg: "", sev: "success" });
  const showSnack = (msg, sev = "success") => setSnackbar({ open: true, msg, sev });

  // ── Cargar estado de campaña ────────────────────────────────────────────────
  useEffect(() => {
    api.get("/configuracion?clave=reclutamiento_activo")
      .then(r => setCampanaActiva(r.data?.valor === true || r.data?.valor === "1" || r.data?.valor === 1))
      .catch(() => {})
      .finally(() => setLoadingCampana(false));
  }, []);

  const toggleCampana = async () => {
    setSavingCampana(true);
    const nuevo = !campanaActiva;
    try {
      await api.put("/configuracion", { clave: "reclutamiento_activo", valor: nuevo ? "1" : "0" });
      setCampanaActiva(nuevo);
      // Al pausar, limpiar sessionStorage para que no quede "ya visto" en cache
      if (!nuevo) sessionStorage.removeItem("recruitment_popup_shown");
      showSnack(nuevo ? "✅ Campaña de reclutamiento activada" : "⏸ Campaña pausada");
    } catch {
      showSnack("Error al cambiar estado de la campaña", "error");
    } finally {
      setSavingCampana(false);
    }
  };

  // ── Candidatos ─────────────────────────────────────────────────────────────
  const cargarPostulaciones = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/postulaciones");
      setPostulaciones(Array.isArray(res.data) ? res.data : []);
    } catch {
      showSnack("Error al cargar postulaciones", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarPostulaciones(); }, [cargarPostulaciones]);

  const counts = postulaciones.reduce((acc, p) => {
    acc[p.estado] = (acc[p.estado] ?? 0) + 1;
    return acc;
  }, {});

  const filtered = postulaciones.filter(p => {
    if (p.estado === "rechazada") return false; // viven en Talento en reserva
    if (p.estado === "aprobada")  return false; // ya forman parte del equipo
    if (filtroEstado !== "todos" && p.estado !== filtroEstado) return false;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      return (p.nombre_completo + p.email + (p.puesto_interes || "") + (p.habilidades || "")).toLowerCase().includes(q);
    }
    return true;
  });

  const rechazados = postulaciones.filter(p => {
    if (p.estado !== "rechazada") return false;
    if (busquedaReserva) {
      const q = busquedaReserva.toLowerCase();
      return (p.nombre_completo + (p.email || "") + (p.puesto_interes || "")).toLowerCase().includes(q);
    }
    return true;
  });

  const cambiarEstado = async (id, nuevoEstado) => {
    setSaving(true);
    try {
      // "Continuar con el proceso" usa endpoint dedicado que también envía correo
      if (nuevoEstado === 'en_proceso') {
        await api.put(`/postulaciones/${id}?accion=continuar_proceso`);
      } else {
        await api.put(`/postulaciones/${id}`, { estado: nuevoEstado });
      }
      await cargarPostulaciones();
      if (selected?.id === id) setSelected(p => ({ ...p, estado: nuevoEstado }));
      showSnack(`Estado actualizado a "${ESTADO_META[nuevoEstado]?.label}"`);
    } catch {
      showSnack("Error al actualizar el estado", "error");
    } finally {
      setSaving(false);
      setConfirmOpen({ open: false, accion: null, id: null });
    }
  };

  // ── Checklist de entrevista ────────────────────────────────────────────────
  const parsedChecklist = useMemo(() => {
    if (!selected?.checklist_data) return null;
    try { return JSON.parse(selected.checklist_data); } catch { return null; }
  }, [selected?.checklist_data]);

  const saveChecklist = async (data) => {
    if (!selected?.id) return;
    setSavingChecklist(true);
    try {
      await api.put(`/postulaciones/${selected.id}?accion=checklist`, { checklist_data: data });
      setSelected(p => ({ ...p, checklist_data: JSON.stringify(data) }));
      showSnack("✅ Checklist guardado");
    } catch {
      showSnack("Error al guardar el checklist", "error");
    } finally {
      setSavingChecklist(false);
    }
  };

  const rechazarPostulacion = async (id, motivo) => {
    setSaving(true);
    try {
      await api.put(`/postulaciones/${id}?accion=rechazar`, { motivo });
      await cargarPostulaciones();
      if (selected?.id === id) {
        setSelected(p => ({ ...p, estado: "rechazada", notas_admin: motivo || p.notas_admin }));
        setDrawerOpen(false);
      }
      showSnack("Postulación rechazada. Se envió retroalimentación al candidato.");
    } catch {
      showSnack("Error al rechazar la postulación", "error");
    } finally {
      setSaving(false);
      setRechazoOpen(false);
      setRechazoMotivo("");
      setRechazoId(null);
    }
  };

  const aprobarPostulacion = async (id) => {
    setSaving(true);
    try {
      const res = await api.put(`/postulaciones/${id}?accion=aprobar`, {});
      if (res.data?.success) {
        await cargarPostulaciones();
        if (selected?.id === id) setSelected(p => ({ ...p, estado: "aprobada" }));
        showSnack(`✅ Aprobado. Usuario "${res.data.username}" creado y credenciales enviadas.`);
      } else {
        showSnack(res.data?.error || "Error al aprobar", "error");
      }
    } catch (e) {
      showSnack(e.response?.data?.error || "Error al aprobar", "error");
    } finally {
      setSaving(false);
      setConfirmOpen({ open: false, accion: null, id: null });
    }
  };

  const agendarEntrevista = async () => {
    if (!entrevistaForm.fecha) { showSnack("Selecciona una fecha", "warning"); return; }
    setSaving(true);
    try {
      const res = await api.put(`/postulaciones/${selected.id}?accion=entrevista`, entrevistaForm);
      if (res.data?.success) {
        await cargarPostulaciones();
        setSelected(p => ({ ...p, estado: "en_entrevista", fecha_entrevista: `${entrevistaForm.fecha}T${entrevistaForm.hora}:00` }));
        setEntrevistaOpen(false);
        showSnack("✅ Entrevista agendada. Se notificó al candidato por email.");
      } else {
        showSnack(res.data?.error || "Error al agendar entrevista", "error");
      }
    } catch {
      showSnack("Error al agendar entrevista", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Vacantes ───────────────────────────────────────────────────────────────
  const cargarVacantes = useCallback(async () => {
    setLoadingVacantes(true);
    try {
      const r = await api.get("/vacantes/todas");
      setVacantes(Array.isArray(r.data) ? r.data : []);
    } catch {
      showSnack("Error al cargar vacantes", "error");
    } finally {
      setLoadingVacantes(false);
    }
  }, []);

  // Carga inicial (sin esperar cambio de tab)
  useEffect(() => { cargarVacantes(); }, [cargarVacantes]);
  // Recarga al cambiar a la pestaña de puestos
  useEffect(() => { if (tabRH === 1) cargarVacantes(); }, [tabRH, cargarVacantes]);

  const openNuevaVacante = () => { setVacanteForm(VACANTE_INIT); setVacanteEditId(null); setVacanteDialogOpen(true); };
  const openEditarVacante = (v) => {
    setVacanteForm({ titulo: v.titulo, descripcion: v.descripcion || "", area: v.area || "",
      tipo: v.tipo, requisitos: v.requisitos || "", beneficios: v.beneficios || "",
      activo: !!v.activo, orden: v.orden || 0 });
    setVacanteEditId(v.id);
    setVacanteDialogOpen(true);
  };

  const guardarVacante = async () => {
    if (!vacanteForm.titulo.trim()) { showSnack("El título es requerido", "warning"); return; }
    setSavingVacante(true);
    try {
      if (vacanteEditId) {
        await api.put(`/vacantes/${vacanteEditId}`, vacanteForm);
        showSnack("✅ Vacante actualizada");
      } else {
        await api.post("/vacantes", vacanteForm);
        showSnack("✅ Vacante creada");
      }
      setVacanteDialogOpen(false);
      cargarVacantes();
    } catch {
      showSnack("Error al guardar vacante", "error");
    } finally {
      setSavingVacante(false);
    }
  };

  const toggleVacante = async (v) => {
    try {
      await api.put(`/vacantes/${v.id}`, { ...v, activo: !v.activo });
      cargarVacantes();
      showSnack(v.activo ? "Vacante pausada" : "Vacante activada");
    } catch { showSnack("Error al actualizar vacante", "error"); }
  };

  const eliminarVacante = async () => {
    if (!confirmDeleteVacante) return;
    try {
      await api.delete(`/vacantes/${confirmDeleteVacante.id}`);
      setConfirmDeleteVacante(null);
      cargarVacantes();
      showSnack("Vacante eliminada");
    } catch { showSnack("Error al eliminar", "error"); }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  // Sección activa resuelta
  // ── Datos de entrevistas ─────────────────────────────────────────────────────────────────────
  const todayStr         = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD
  const entrevistas      = [...postulaciones]
    .filter(p => p.estado === "en_entrevista" || !!p.fecha_entrevista)
    .sort((a, b) => {
      if (!a.fecha_entrevista) return 1;
      if (!b.fecha_entrevista) return -1;
      return new Date(a.fecha_entrevista.replace(" ", "T")) - new Date(b.fecha_entrevista.replace(" ", "T"));
    });
  const entrevistasHoy     = entrevistas.filter(p => getFechaDate(p.fecha_entrevista) === todayStr);
  const entrevistasFuturas = entrevistas.filter(p => { const d = getFechaDate(p.fecha_entrevista); return d && d > todayStr; });
  const entrevistasPasadas = entrevistas.filter(p => { const d = getFechaDate(p.fecha_entrevista); return !d || d < todayStr; });

  const seccion = activeSection ?? (tabRH === 0 ? "candidatos" : tabRH === 1 ? "puestos" : tabRH === 2 ? "entrevistas" : "talento_reserva");

  return (
    <Box>
      {/* ── Header principal (solo en modo autónomo) ── */}
      {!activeSection && <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
            <Groups sx={{ color: "#6366f1" }} /> Gestión de Talento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pipeline de selección · {postulaciones.length} postulación{postulaciones.length !== 1 ? "es" : ""} en total
          </Typography>
        </Box>

        {/* Toggle campaña */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "10px 16px", borderRadius: "14px",
          background: campanaActiva ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.04)",
          border: campanaActiva ? "1px solid rgba(99,102,241,0.25)" : "1px solid rgba(255,255,255,0.08)",
          transition: "all 0.3s" }}>
          <CampaignOutlined sx={{ color: campanaActiva ? "#6366f1" : "text.disabled", fontSize: 22 }} />
          <Box sx={{ mr: 0.5 }}>
            <Typography variant="caption" sx={{ color: "text.disabled", display: "block", lineHeight: 1 }}>
              Campaña de reclutamiento
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: campanaActiva ? "#a5b4fc" : "text.secondary", mt: 0.2 }}>
              {loadingCampana ? "Cargando…" : campanaActiva ? "🟢 Activa — visible en login" : "🔴 Pausada — oculta en login"}
            </Typography>
          </Box>
          {loadingCampana || savingCampana
            ? <CircularProgress size={22} sx={{ color: "#6366f1" }} />
            : <Switch checked={campanaActiva} onChange={toggleCampana}
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#6366f1" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#6366f1" } }} />
          }
        </Box>
      </Box>}

      {/* ── Tabs internos (solo en modo autónomo) ── */}
      {!activeSection && <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.08)", mb: 3 }}>
        <Tabs value={tabRH} onChange={(_, v) => setTabRH(v)}
          sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
            "& .MuiTabs-indicator": { bgcolor: "#6366f1" },
            "& .Mui-selected": { color: "#a5b4fc !important" } }}>
          <Tab label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Inbox sx={{ fontSize: 18 }} /> Candidatos
              {(counts.pendiente ?? 0) > 0 && (
                <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "#f59e0b",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, color: "#000" }}>{counts.pendiente}</Typography>
                </Box>
              )}
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BusinessCenter sx={{ fontSize: 18 }} /> Puestos disponibles
              <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, color: "text.secondary" }}>
                  {vacantes.filter(v => v.activo).length}
                </Typography>
              </Box>
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventAvailable sx={{ fontSize: 18 }} /> Entrevistas
              {entrevistasHoy.length > 0 && (
                <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "#f59e0b",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, color: "#000" }}>{entrevistasHoy.length}</Typography>
                </Box>
              )}
              {entrevistasHoy.length === 0 && entrevistas.length > 0 && (
                <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "rgba(139,92,246,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, color: "#a78bfa" }}>{entrevistas.length}</Typography>
                </Box>
              )}
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BookmarkBorder sx={{ fontSize: 18 }} /> Talento en reserva
              {(counts.rechazada ?? 0) > 0 && (
                <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "rgba(239,68,68,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, color: "#ef4444" }}>{counts.rechazada ?? 0}</Typography>
                </Box>
              )}
            </Box>
          } />
        </Tabs>
      </Box>}

      {/* ════════════════════════════════════════════════════════════════════
          SECCIÓN: OVERVIEW (campaña + KPIs)
      ════════════════════════════════════════════════════════════════════ */}
      {seccion === "overview" && (
        <Box>
          {/* Toggle campaña */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: "16px 20px", mb: 3, borderRadius: "16px",
            background: campanaActiva ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.04)",
            border: campanaActiva ? "1px solid rgba(99,102,241,0.25)" : "1px solid rgba(255,255,255,0.08)",
            transition: "all 0.3s", flexWrap: "wrap" }}>
            <CampaignOutlined sx={{ color: campanaActiva ? "#6366f1" : "text.disabled", fontSize: 28, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: campanaActiva ? "#a5b4fc" : "text.secondary" }}>
                Campaña de reclutamiento
              </Typography>
              <Typography variant="body2" sx={{ color: "text.disabled", mt: 0.2 }}>
                {loadingCampana ? "Cargando…" : campanaActiva
                  ? "🟢 Activa — el popup de postulación se muestra en el login"
                  : "🔴 Pausada — el popup está oculto para los visitantes"}
              </Typography>
            </Box>
            {loadingCampana || savingCampana
              ? <CircularProgress size={24} sx={{ color: "#6366f1" }} />
              : <Switch checked={campanaActiva} onChange={toggleCampana}
                  sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#6366f1" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#6366f1" } }} />
            }
          </Box>
          {/* 🔔 Notificación entrevistas del día */}
          {entrevistasHoy.length > 0 && (
            <Box onClick={() => setTabRH(2)} sx={{
              display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer",
              p: "10px 14px", borderRadius: "12px", mb: 2,
              bgcolor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.28)",
              transition: "all 0.2s", "&:hover": { bgcolor: "rgba(245,158,11,0.14)" },
            }}>
              <NotificationsActive sx={{ fontSize: 20, color: "#f59e0b", flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#fbbf24" }}>
                  {entrevistasHoy.length === 1 ? "1 entrevista programada para HOY" : `${entrevistasHoy.length} entrevistas programadas para HOY`}
                </Typography>
                <Typography variant="caption" sx={{ color: "#d97706" }}>
                  {new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}
                </Typography>
              </Box>
              <ArrowForward sx={{ fontSize: 16, color: "#f59e0b", flexShrink: 0 }} />
            </Box>
          )}
          {/* KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { icon: <Inbox />, label: "Total recibidas", value: postulaciones.length, color: "#8b5cf6", sublabel: "postulaciones" },
              { icon: <HourglassTop />, label: "Pendientes", value: counts.pendiente ?? 0, color: "#f59e0b", sublabel: "esperando revisión" },
              { icon: <CalendarMonth />, label: "En entrevista", value: counts.en_entrevista ?? 0, color: "#8b5cf6", sublabel: "citas agendadas" },
              { icon: <AssignmentTurnedIn />, label: "Seleccionados", value: counts.aprobada ?? 0, color: "#22c55e", sublabel: "aprobados" },
            ].map((k, i) => (
              <Grid item xs={6} md={3} key={i} sx={{ display: "flex" }}><KPICard {...k} /></Grid>
            ))}
          </Grid>
          {postulaciones.length > 0 && <PipelineBar counts={counts} />}
          {/* Resumen puestos */}
          <Box sx={{ mt: 3, p: "14px 18px", borderRadius: "12px",
            background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              <BusinessCenter sx={{ fontSize: 15, mr: 0.5, verticalAlign: "middle" }} />
              <strong style={{ color: "rgba(255,255,255,0.7)" }}>{vacantes.filter(v => v.activo).length}</strong> puestos activos ·{" "}
              <strong style={{ color: "rgba(255,255,255,0.4)" }}>{vacantes.filter(v => !v.activo).length}</strong> pausados
            </Typography>
          </Box>
        </Box>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          SECCIÓN: CANDIDATOS
      ════════════════════════════════════════════════════════════════════ */}
      {seccion === "candidatos" && (
        <Box>
          {/* KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { icon: <Inbox />, label: "Total recibidas", value: postulaciones.length, color: "#8b5cf6", sublabel: "postulaciones" },
              { icon: <HourglassTop />, label: "Pendientes", value: counts.pendiente ?? 0, color: "#f59e0b", sublabel: "esperando revisión" },
              { icon: <CalendarMonth />, label: "En entrevista", value: counts.en_entrevista ?? 0, color: "#8b5cf6", sublabel: "citas agendadas" },
              { icon: <Groups />, label: "En proceso", value: counts.en_proceso ?? 0, color: "#06b6d4", sublabel: "pasaron entrevista" },
              { icon: <AssignmentTurnedIn />, label: "Seleccionados", value: counts.aprobada ?? 0, color: "#22c55e", sublabel: "aprobados" },
            ].map((k, i) => (
              <Grid item xs={6} md={3} key={i} sx={{ display: "flex" }}><KPICard {...k} /></Grid>
            ))}
          </Grid>

          {postulaciones.length > 0 && <PipelineBar counts={counts} />}

          {/* Filtros */}
          <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap", alignItems: "center" }}>
            <TextField size="small" placeholder="Buscar candidato…" value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: "text.disabled" }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
            <Box sx={{ display: "flex", gap: 0.7, flexWrap: "wrap" }}>
              {["todos","pendiente","en_revision","en_entrevista","en_proceso"].map(est => (
                <Chip key={est} onClick={() => setFiltroEstado(est)}
                  label={est === "todos" ? `Todos (${postulaciones.length})` : `${ESTADO_META[est]?.label} (${counts[est] ?? 0})`}
                  sx={{ cursor: "pointer", borderRadius: "10px", fontWeight: 600, fontSize: "0.75rem",
                    ...(filtroEstado === est
                      ? { background: est === "todos" ? "#6366f1" : ESTADO_META[est]?.color, color: "#fff" }
                      : { background: "rgba(255,255,255,0.05)", color: "text.secondary" }) }} />
              ))}
            </Box>
            <Tooltip title="Actualizar">
              <IconButton onClick={cargarPostulaciones} disabled={loading}
                sx={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px" }}>
                <Refresh sx={{ fontSize: 18, animation: loading ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } }} />
              </IconButton>
            </Tooltip>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: "#6366f1" }} /></Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <EmojiPeople sx={{ fontSize: 56, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">
                {busqueda || filtroEstado !== "todos" ? "Sin resultados para el filtro aplicado" : "No hay postulaciones aún"}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filtered.map(post => (
                <Grid item xs={12} sm={6} lg={4} key={post.id}>
                  <CandidatoCard post={post} onSelect={p => { setSelected(p); setDrawerOpen(true); setDrawerTab(0); }} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          SECCIÓN: PUESTOS DISPONIBLES
      ════════════════════════════════════════════════════════════════════ */}
      {seccion === "puestos" && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Puestos disponibles</Typography>
              <Typography variant="body2" color="text.secondary">
                {vacantes.filter(v => v.activo).length} activos · {vacantes.filter(v => !v.activo).length} pausados
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="Actualizar lista"><IconButton onClick={cargarVacantes} disabled={loadingVacantes}
                sx={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px" }}>
                <Refresh sx={{ fontSize: 18 }} /></IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openNuevaVacante}
                sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700,
                  background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
                  "&:hover": { background: "linear-gradient(90deg,#4f46e5,#7c3aed)" } }}>
                Nueva vacante
              </Button>
            </Box>
          </Box>

          <Box sx={{ p: 2, mb: 3, borderRadius: "12px", background: "rgba(99,102,241,0.06)",
            border: "1px solid rgba(99,102,241,0.15)", display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <BusinessCenter sx={{ color: "#a5b4fc", mt: 0.2, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}>
              Los puestos <strong style={{ color: "#a5b4fc" }}>activos</strong> aparecen como opciones en el formulario de postulación del popup de reclutamiento.
              Pausa los que ya no tienen cupo sin eliminarlos.
            </Typography>
          </Box>

          {loadingVacantes ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: "#6366f1" }} /></Box>
          ) : vacantes.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <BusinessCenter sx={{ fontSize: 56, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary" sx={{ mb: 2 }}>No hay vacantes creadas</Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={openNuevaVacante}
                sx={{ borderRadius: "12px", textTransform: "none", borderColor: "rgba(99,102,241,0.4)", color: "#a5b4fc" }}>
                Crear primera vacante
              </Button>
            </Box>
          ) : (
            <>
              {/* ── DESKTOP: tabla ── */}
              <Box sx={{ display: { xs: "none", md: "block" }, overflowX: "auto",
                borderRadius: "16px", border: "1px solid rgba(255,255,255,0.07)",
                background: "rgba(255,255,255,0.02)" }}>
                <Table size="small" sx={{ minWidth: 680 }}>
                  <TableHead>
                    <TableRow sx={{ "& th": { borderBottom: "1px solid rgba(255,255,255,0.08)",
                      py: 1.5, px: 2, color: "rgba(255,255,255,0.4)",
                      fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                      background: "rgba(99,102,241,0.04)" } }}>
                      <TableCell>#</TableCell>
                      <TableCell>Vacante / Área</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Requisitos</TableCell>
                      <TableCell>Beneficios</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vacantes.map((v, idx) => {
                      const tipo = TIPO_META[v.tipo] ?? TIPO_META.por_evento;
                      return (
                        <TableRow key={v.id} sx={{ opacity: v.activo ? 1 : 0.5,
                          "& td": { borderBottom: "1px solid rgba(255,255,255,0.05)", py: 1.4, px: 2 },
                          "&:last-child td": { borderBottom: "none" },
                          "&:hover": { background: "rgba(99,102,241,0.04)" }, transition: "background 0.15s" }}>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 600 }}>{idx + 1}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3, color: v.activo ? "text.primary" : "text.disabled" }}>
                              {v.titulo}
                            </Typography>
                            {v.area && (
                              <Typography variant="caption" sx={{ color: "text.disabled", display: "flex", alignItems: "center", gap: 0.4, mt: 0.2 }}>
                                <BusinessCenter sx={{ fontSize: 11 }} />{v.area}
                              </Typography>
                            )}
                            {v.descripcion && (
                              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", display: "-webkit-box",
                                WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden", mt: 0.2 }}>
                                {v.descripcion}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={tipo.label} size="small" sx={{ borderRadius: "8px", fontSize: "0.67rem", fontWeight: 700, height: 22,
                              background: `${tipo.color}18`, color: tipo.color, border: `1px solid ${tipo.color}30` }} />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", display: "-webkit-box",
                              WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5 }}>
                              {v.requisitos || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", display: "-webkit-box",
                              WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5 }}>
                              {v.beneficios || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={v.activo ? "Activa" : "Pausada"} size="small"
                              sx={{ borderRadius: "20px", fontSize: "0.67rem", fontWeight: 700, height: 22,
                                background: v.activo ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.05)",
                                color: v.activo ? "#22c55e" : "rgba(255,255,255,0.3)",
                                border: `1px solid ${v.activo ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.08)"}` }} />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                              <Tooltip title={v.activo ? "Pausar" : "Activar"}>
                                <IconButton size="small" onClick={() => toggleVacante(v)}
                                  sx={{ color: v.activo ? "#f59e0b" : "#22c55e",
                                    background: v.activo ? "rgba(245,158,11,0.1)" : "rgba(34,197,94,0.1)",
                                    borderRadius: "8px", p: 0.7,
                                    "&:hover": { background: v.activo ? "rgba(245,158,11,0.2)" : "rgba(34,197,94,0.2)" } }}>
                                  {v.activo ? <PauseCircle sx={{ fontSize: 16 }} /> : <PlayCircle sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar">
                                <IconButton size="small" onClick={() => openEditarVacante(v)}
                                  sx={{ color: "#60a5fa", background: "rgba(96,165,250,0.1)", borderRadius: "8px", p: 0.7,
                                    "&:hover": { background: "rgba(96,165,250,0.2)" } }}>
                                  <Edit sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton size="small" onClick={() => setConfirmDeleteVacante(v)}
                                  sx={{ color: "#ef4444", background: "rgba(239,68,68,0.08)", borderRadius: "8px", p: 0.7,
                                    "&:hover": { background: "rgba(239,68,68,0.15)" } }}>
                                  <Delete sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              {/* ── MÓVIL: tarjetas desplazables ── */}
              <Box sx={{ display: { xs: "flex", md: "none" }, gap: 2,
                overflowX: "auto", pb: 1,
                "&::-webkit-scrollbar": { height: 4 },
                "&::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.03)" },
                "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.12)", borderRadius: 4 } }}>
                {vacantes.map(v => {
                  const tipo = TIPO_META[v.tipo] ?? TIPO_META.por_evento;
                  return (
                    <Box key={v.id} sx={{ minWidth: 280, maxWidth: 300, flexShrink: 0,
                      borderRadius: "16px", border: v.activo ? "1px solid rgba(255,255,255,0.1)" : "1px dashed rgba(255,255,255,0.07)",
                      background: v.activo ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.2)",
                      p: 2, display: "flex", flexDirection: "column", gap: 1.2 }}>
                      {/* Header */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.3, color: v.activo ? "text.primary" : "text.disabled", flex: 1 }}>
                          {v.titulo}
                        </Typography>
                        <Chip label={v.activo ? "Activa" : "Pausada"} size="small"
                          sx={{ borderRadius: "20px", fontSize: "0.63rem", fontWeight: 700, height: 20, flexShrink: 0,
                            background: v.activo ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.05)",
                            color: v.activo ? "#22c55e" : "rgba(255,255,255,0.3)",
                            border: `1px solid ${v.activo ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.08)"}` }} />
                      </Box>
                      {/* Área + tipo */}
                      <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
                        <Chip label={tipo.label} size="small" sx={{ borderRadius: "8px", fontSize: "0.65rem", fontWeight: 700, height: 20,
                          background: `${tipo.color}18`, color: tipo.color, border: `1px solid ${tipo.color}30` }} />
                        {v.area && (
                          <Chip label={v.area} size="small" sx={{ borderRadius: "8px", fontSize: "0.65rem", height: 20,
                            background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)",
                            border: "1px solid rgba(255,255,255,0.1)" }} />
                        )}
                      </Box>
                      {/* Descripción */}
                      {v.descripcion && (
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", lineHeight: 1.5,
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {v.descripcion}
                        </Typography>
                      )}
                      <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
                      {/* Requisitos */}
                      {v.requisitos && (
                        <Box>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.6rem" }}>Requisitos</Typography>
                          <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.5)", lineHeight: 1.5, mt: 0.3 }}>
                            {v.requisitos}
                          </Typography>
                        </Box>
                      )}
                      {/* Beneficios */}
                      {v.beneficios && (
                        <Box>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.6rem" }}>Beneficios</Typography>
                          <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.5)", lineHeight: 1.5, mt: 0.3 }}>
                            {v.beneficios}
                          </Typography>
                        </Box>
                      )}
                      {/* Acciones */}
                      <Box sx={{ display: "flex", gap: 0.8, mt: "auto", pt: 0.5 }}>
                        <Tooltip title={v.activo ? "Pausar" : "Activar"}>
                          <IconButton size="small" onClick={() => toggleVacante(v)}
                            sx={{ color: v.activo ? "#f59e0b" : "#22c55e",
                              background: v.activo ? "rgba(245,158,11,0.1)" : "rgba(34,197,94,0.1)",
                              borderRadius: "8px", p: 0.7, flex: 1,
                              "&:hover": { background: v.activo ? "rgba(245,158,11,0.2)" : "rgba(34,197,94,0.2)" } }}>
                            {v.activo ? <PauseCircle sx={{ fontSize: 18 }} /> : <PlayCircle sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEditarVacante(v)}
                            sx={{ color: "#60a5fa", background: "rgba(96,165,250,0.1)", borderRadius: "8px", p: 0.7, flex: 1,
                              "&:hover": { background: "rgba(96,165,250,0.2)" } }}>
                            <Edit sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => setConfirmDeleteVacante(v)}
                            sx={{ color: "#ef4444", background: "rgba(239,68,68,0.08)", borderRadius: "8px", p: 0.7, flex: 1,
                              "&:hover": { background: "rgba(239,68,68,0.15)" } }}>
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          SECCIÓN: ENTREVISTAS POR REALIZAR
      ════════════════════════════════════════════════════════════════════ */}
      {seccion === "entrevistas" && (
        <Box>
          {entrevistasHoy.length > 0 && (
            <Alert
              icon={<NotificationsActive sx={{ fontSize: 20 }} />}
              severity="warning"
              sx={{
                mb: 3, borderRadius: "14px", bgcolor: "rgba(245,158,11,0.07)",
                color: "#fbbf24", border: "1px solid rgba(245,158,11,0.28)",
                "& .MuiAlert-icon": { color: "#f59e0b", alignItems: "center" },
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>
                {entrevistasHoy.length === 1
                  ? "Tienes 1 entrevista programada para HOY"
                  : `Tienes ${entrevistasHoy.length} entrevistas programadas para HOY`}
              </Typography>
              <Typography variant="caption" sx={{ color: "#d97706", display: "block", mt: 0.2 }}>
                {new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </Typography>
            </Alert>
          )}
          {entrevistas.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <EventAvailable sx={{ fontSize: 60, color: "text.disabled", mb: 1.5 }} />
              <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 600 }}>Sin entrevistas pendientes</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>Cuando agendes una entrevista aparecerá aquí</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
                {[
                  { label: "Hoy",       value: entrevistasHoy.length,      color: "#f59e0b" },
                  { label: "Próximas",  value: entrevistasFuturas.length,   color: "#8b5cf6" },
                  { label: "Vencidas",  value: entrevistasPasadas.length,   color: "#6b7280" },
                ].map(s => (
                  <Box key={s.label} sx={{
                    display: "flex", alignItems: "center", gap: 0.8, px: 1.5, py: 0.8, borderRadius: "10px",
                    bgcolor: s.value > 0 ? s.color + "15" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${s.value > 0 ? s.color + "30" : "rgba(255,255,255,0.06)"}`,
                  }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: s.value > 0 ? s.color : "#4b5563" }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: s.value > 0 ? s.color : "text.disabled" }}>{s.label}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: s.value > 0 ? s.color : "text.disabled" }}>{s.value}</Typography>
                  </Box>
                ))}
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Actualizar">
                  <IconButton size="small" onClick={cargarPostulaciones} disabled={loading}
                    sx={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px" }}>
                    <Refresh sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <InterviewGroup label="Hoy" icon="📅" items={entrevistasHoy} isToday isPast={false} onOpen={p => { setSelected(p); setDrawerOpen(true); setDrawerTab(0); }} />
              <InterviewGroup label="Próximas" icon="🗓" items={entrevistasFuturas} isToday={false} isPast={false} onOpen={p => { setSelected(p); setDrawerOpen(true); setDrawerTab(0); }} />
              <InterviewGroup label="Vencidas — sin confirmar" icon="⚠️" items={entrevistasPasadas} isToday={false} isPast onOpen={p => { setSelected(p); setDrawerOpen(true); setDrawerTab(0); }} />
            </>
          )}
        </Box>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          SECCIÓN: TALENTO EN RESERVA
      ════════════════════════════════════════════════════════════════════ */}
      {seccion === "talento_reserva" && (
        <Box>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                <BookmarkBorder sx={{ color: "#ef4444" }} /> Talento en reserva
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Candidatos que no avanzaron en el proceso · {counts.rechazada ?? 0} perfiles guardados
              </Typography>
            </Box>
            <Tooltip title="Actualizar">
              <IconButton onClick={cargarPostulaciones} disabled={loading}
                sx={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px" }}>
                <Refresh sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Info */}
          <Box sx={{ mb: 3, p: "12px 16px", borderRadius: "12px",
            background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.12)" }}>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", lineHeight: 1.7 }}>
              <BookmarkBorder sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle", color: "#ef4444" }} />
              Los perfiles en reserva son candidatos que no avanzaron en el proceso actual.
              Sus datos quedan guardados para considerar en futuras convocatorias.
              Puedes <strong style={{ color: "rgba(255,255,255,0.7)" }}>reactivarlos</strong> en cualquier momento.
            </Typography>
          </Box>

          {/* Buscador */}
          <TextField size="small" fullWidth placeholder="Buscar en talento en reserva…" value={busquedaReserva}
            onChange={e => setBusquedaReserva(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: "text.disabled" }} /></InputAdornment> }}
            sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: "#ef4444" }} /></Box>
          ) : rechazados.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <BookmarkBorder sx={{ fontSize: 60, color: "text.disabled", mb: 1.5 }} />
              <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 600 }}>Sin perfiles en reserva</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                {busquedaReserva ? "Sin coincidencias para la búsqueda" : "Los candidatos no seleccionados aparecerán aquí"}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {rechazados.map(p => (
                <Box key={p.id} onClick={() => { setSelected(p); setDrawerOpen(true); setDrawerTab(0); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: "14px",
                    cursor: "pointer", background: "rgba(255,255,255,0.02)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    transition: "all 0.2s",
                    "&:hover": { background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.25)", transform: "translateX(4px)" },
                  }}>
                  <Avatar sx={{ width: 42, height: 42, bgcolor: avatarColor(p.nombre_completo),
                    fontWeight: 700, fontSize: "0.85rem", flexShrink: 0, opacity: 0.75 }}>
                    {getInitials(p.nombre_completo)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "rgba(255,255,255,0.75)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.nombre_completo}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, mt: 0.3, flexWrap: "wrap" }}>
                      {p.puesto_interes && (
                        <Typography variant="caption" sx={{ color: "rgba(165,180,252,0.65)", display: "flex", alignItems: "center", gap: 0.3 }}>
                          <Work sx={{ fontSize: 11 }} />{p.puesto_interes}
                        </Typography>
                      )}
                      {p.ciudad && (
                        <Typography variant="caption" sx={{ color: "text.disabled", display: "flex", alignItems: "center", gap: 0.3 }}>
                          <LocationOn sx={{ fontSize: 11 }} />{p.ciudad}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.4 }}>
                    <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.7rem" }}>
                      {fmtDate(p.created_at)}
                    </Typography>
                    {p.notas_admin && (
                      <Typography variant="caption"
                        sx={{ color: "rgba(239,68,68,0.6)", maxWidth: 180, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "right", fontSize: "0.68rem" }}>
                        💬 {p.notas_admin}
                      </Typography>
                    )}
                  </Box>
                  <ArrowForward sx={{ fontSize: 16, color: "rgba(255,255,255,0.2)", flexShrink: 0, ml: 0.5 }} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          DRAWER: Perfil del candidato
      ══════════════════════════════════════════════════════════════════════ */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: "100vw", sm: 480 }, background: "#0a0f1e", borderLeft: "1px solid rgba(255,255,255,0.08)" } }}>
        {selected && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ p: 3, pb: 2, background: "linear-gradient(135deg,#0f1630,#0d1428)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                <Chip icon={ESTADO_META[selected.estado]?.icon} label={ESTADO_META[selected.estado]?.label}
                  sx={{ borderRadius: "10px", fontWeight: 700, fontSize: "0.78rem",
                    background: ESTADO_META[selected.estado]?.bg, color: ESTADO_META[selected.estado]?.color,
                    border: `1px solid ${ESTADO_META[selected.estado]?.color}40`,
                    "& .MuiChip-icon": { color: ESTADO_META[selected.estado]?.color } }} />
                <IconButton onClick={() => setDrawerOpen(false)} size="small" sx={{ color: "text.secondary" }}><Close /></IconButton>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: avatarColor(selected.nombre_completo),
                  fontWeight: 800, fontSize: "1.4rem", boxShadow: `0 0 0 3px ${avatarColor(selected.nombre_completo)}40` }}>
                  {getInitials(selected.nombre_completo)}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{selected.nombre_completo}</Typography>
                  {selected.puesto_interes && (
                    <Typography variant="body2" sx={{ color: "#a5b4fc", mt: 0.3 }}>
                      <Work sx={{ fontSize: 13, mr: 0.5 }} />{selected.puesto_interes}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    Postulación #{selected.id} · {fmtDate(selected.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                <Button size="small" startIcon={<Email sx={{ fontSize: 15 }} />}
                  onClick={() => window.open(`mailto:${selected.email}`, "_blank")}
                  sx={{ borderRadius: "10px", textTransform: "none", fontSize: "0.78rem",
                    background: "rgba(96,165,250,0.12)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.2)" }}>
                  Email
                </Button>
                {selected.telefono && (
                  <Button size="small" startIcon={<WhatsApp sx={{ fontSize: 15 }} />}
                    onClick={() => window.open(`https://wa.me/${selected.telefono.replace(/\D/g,'')}?text=Hola ${selected.nombre_completo}, somos A La Vuelta 360°.`, "_blank")}
                    sx={{ borderRadius: "10px", textTransform: "none", fontSize: "0.78rem",
                      background: "rgba(37,211,102,0.1)", color: "#25D366", border: "1px solid rgba(37,211,102,0.2)" }}>
                    WhatsApp
                  </Button>
                )}
                {selected.linkedin_url && (
                  <Button size="small" startIcon={<LinkedIn sx={{ fontSize: 15 }} />}
                    onClick={() => window.open(selected.linkedin_url, "_blank")}
                    sx={{ borderRadius: "10px", textTransform: "none", fontSize: "0.78rem",
                      background: "rgba(10,102,194,0.1)", color: "#0a66c2", border: "1px solid rgba(10,102,194,0.2)" }}>
                    LinkedIn
                  </Button>
                )}
                {selected.portfolio_url && (
                  <Button size="small" startIcon={<Language sx={{ fontSize: 15 }} />}
                    onClick={() => window.open(selected.portfolio_url, "_blank")}
                    sx={{ borderRadius: "10px", textTransform: "none", fontSize: "0.78rem",
                      background: "rgba(255,255,255,0.05)", color: "text.secondary", border: "1px solid rgba(255,255,255,0.1)" }}>
                    Portfolio
                  </Button>
                )}
              </Box>
            </Box>

            {/* ── Tabs: Perfil | Checklist | Guía ── */}
            <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.07)", bgcolor: "rgba(0,0,0,0.2)" }}>
              <Tabs
                value={drawerTab}
                onChange={(_, v) => setDrawerTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 42,
                  "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.78rem", minHeight: 42, py: 0, px: 1.5 },
                  "& .MuiTabs-indicator": { bgcolor: "#6366f1", height: 2 },
                  "& .Mui-selected": { color: "#a5b4fc !important" },
                }}
              >
                <Tab label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Person sx={{ fontSize: 15 }} /> Perfil
                  </Box>
                } />
                <Tab label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Assignment sx={{ fontSize: 15 }} />
                    Checklist
                    {selected?.checklist_data && (
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#22c55e", ml: 0.4, flexShrink: 0 }} />
                    )}
                  </Box>
                } />
                <Tab label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <QuestionAnswer sx={{ fontSize: 15 }} />
                    Guía de preguntas
                  </Box>
                } />
                <Tab label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <ArticleOutlined sx={{ fontSize: 15 }} />
                    CV
                    {selected?.cv_url && (
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#6366f1", ml: 0.4, flexShrink: 0 }} />
                    )}
                  </Box>
                } />
              </Tabs>
            </Box>

            {/* ── Tab 0: Perfil del candidato ── */}
            {drawerTab === 0 && (
            <Box sx={{ flex: 1, overflowY: "auto", p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <Typography variant="overline" sx={{ color: "text.disabled", letterSpacing: "0.1em" }}>Datos de contacto</Typography>
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.2 }}>
                  {[
                    { icon: <Email sx={{ fontSize: 16 }} />, label: "Email", val: selected.email },
                    { icon: <Phone sx={{ fontSize: 16 }} />, label: "Teléfono", val: selected.telefono || "—" },
                    { icon: <LocationOn sx={{ fontSize: 16 }} />, label: "Ciudad", val: selected.ciudad || "—" },
                    { icon: <HomeOutlined sx={{ fontSize: 16 }} />, label: "Dirección", val: selected.direccion || "—" },
                    { icon: <Person sx={{ fontSize: 16 }} />, label: "Fecha nac.", val: selected.fecha_nacimiento ? fmtDate(selected.fecha_nacimiento) : "—" },
                  ].map(r => (
                    <Box key={r.label} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box sx={{ color: "#6366f1", mt: 0.1, flexShrink: 0 }}>{r.icon}</Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "text.disabled", lineHeight: 1 }}>{r.label}</Typography>
                        <Typography variant="body2">{r.val}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />

              <Box>
                <Typography variant="overline" sx={{ color: "text.disabled", letterSpacing: "0.1em" }}>Perfil profesional</Typography>
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Box sx={{ color: "#6366f1", mt: 0.1 }}><Work sx={{ fontSize: 16 }} /></Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>Puesto de interés</Typography>
                      <Typography variant="body2">{selected.puesto_interes || "—"}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>Experiencia</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                      <LinearProgress variant="determinate"
                        value={Math.min(((selected.experiencia_anos || 0) / 5) * 100, 100)}
                        sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.06)",
                          "& .MuiLinearProgress-bar": { bgcolor: "#6366f1", borderRadius: 3 } }} />
                      <Typography variant="caption" sx={{ color: "#a5b4fc", flexShrink: 0 }}>
                        {EXP_LABEL[selected.experiencia_anos] || `${selected.experiencia_anos} años`}
                      </Typography>
                    </Box>
                  </Box>
                  {selected.disponibilidad && (
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>Disponibilidad</Typography>
                      <Typography variant="body2">{DISPONIBILIDAD_LABEL[selected.disponibilidad]}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {selected.habilidades && (
                <>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
                  <Box>
                    <Typography variant="overline" sx={{ color: "text.disabled", letterSpacing: "0.1em", mb: 1.5, display: "block" }}>Habilidades</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                      {selected.habilidades.split(",").map(s => s.trim()).filter(Boolean).map(s => <SkillChip key={s} label={s} />)}
                    </Box>
                  </Box>
                </>
              )}

              {selected.descripcion_exp && (
                <>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
                  <Box>
                    <Typography variant="overline" sx={{ color: "text.disabled", letterSpacing: "0.1em" }}>Experiencia previa</Typography>
                    <Box sx={{ mt: 1.2, p: 2, borderRadius: "12px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.8, color: "text.secondary", whiteSpace: "pre-wrap" }}>{selected.descripcion_exp}</Typography>
                    </Box>
                  </Box>
                </>
              )}

              {selected.mensaje && (
                <>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
                  <Box>
                    <Typography variant="overline" sx={{ color: "text.disabled", letterSpacing: "0.1em" }}>¿Por qué A La Vuelta 360°?</Typography>
                    <Box sx={{ mt: 1.2, p: 2, borderRadius: "12px",
                      background: "linear-gradient(135deg,rgba(99,102,241,0.06),rgba(139,92,246,0.06))",
                      border: "1px solid rgba(99,102,241,0.12)" }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.8, color: "text.secondary", fontStyle: "italic" }}>"{selected.mensaje}"</Typography>
                    </Box>
                  </Box>
                </>
              )}

              {selected.fecha_entrevista && (
                <>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
                  <Box sx={{ p: 2, borderRadius: "12px", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                    <Typography variant="caption" sx={{ color: "#60a5fa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>📅 Entrevista agendada</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>{fmtDateTime(selected.fecha_entrevista)}</Typography>
                    {selected.lugar_entrevista && <Typography variant="caption" sx={{ color: "text.secondary" }}>📍 {selected.lugar_entrevista}</Typography>}
                  </Box>
                </>
              )}

              {selected.estado === "rechazada" && selected.notas_admin && (
                <>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
                  <Box sx={{ p: 2, borderRadius: "12px", background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)" }}>
                    <Typography variant="caption" sx={{ color: "#ef4444", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 0.8 }}>
                      💬 Retroalimentación enviada
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.8, fontStyle: "italic" }}>"{selected.notas_admin}"</Typography>
                  </Box>
                </>
              )}
            </Box>
            )}

            {/* ── Tab 1: Checklist de entrevista ── */}
            {drawerTab === 1 && (
              <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                <InterviewChecklist
                  candidato={selected}
                  initialData={parsedChecklist}
                  onSave={saveChecklist}
                  saving={savingChecklist}
                />
              </Box>
            )}

            {/* ── Tab 2: Guía de preguntas recomendadas ── */}
            {drawerTab === 2 && (
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                <InterviewQuestionsGuide candidato={selected} />
              </Box>
            )}

            {/* ── Tab 3: CV del candidato ── */}
            {drawerTab === 3 && (
              <Box sx={{ flex: 1, overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                {selected.cv_url ? (() => {
                  const cvHref = `/${selected.cv_url}`;
                  const isPdf  = selected.cv_url.toLowerCase().endsWith(".pdf");
                  return (
                    <>
                      {/* Cabecera + botón */}
                      <Box sx={{
                        p: 2, borderRadius: "14px",
                        background: "linear-gradient(135deg,rgba(99,102,241,0.12),rgba(139,92,246,0.08))",
                        border: "1px solid rgba(99,102,241,0.25)",
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.8 }}>
                          <ArticleOutlined sx={{ color: "#a5b4fc", fontSize: 30 }} />
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: "#a5b4fc", lineHeight: 1.2 }}>
                              Currículum Vitae
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.disabled" }}>
                              {isPdf ? "Formato PDF · previsualización disponible" : "Formato Word · solo descarga"}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          component="a"
                          href={cvHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          fullWidth
                          variant="contained"
                          startIcon={<ArticleOutlined />}
                          sx={{
                            borderRadius: "12px", textTransform: "none", fontWeight: 700, fontSize: "0.9rem",
                            background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
                            boxShadow: "0 4px 20px rgba(99,102,241,0.35)",
                            "&:hover": {
                              background: "linear-gradient(90deg,#4f46e5,#7c3aed)",
                              boxShadow: "0 6px 28px rgba(99,102,241,0.5)",
                              transform: "translateY(-1px)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          Abrir / Descargar CV
                        </Button>
                      </Box>

                      {/* Previsualización — solo para PDF */}
                      {isPdf ? (
                        <Box sx={{
                          flex: 1, borderRadius: "12px", overflow: "hidden",
                          border: "1px solid rgba(255,255,255,0.08)",
                          minHeight: 440, bgcolor: "#fff", position: "relative",
                        }}>
                          <object
                            data={cvHref}
                            type="application/pdf"
                            width="100%"
                            style={{ height: "100%", minHeight: 440, border: "none", display: "block" }}
                          >
                            {/* Fallback si el navegador no puede renderizar el PDF */}
                            <Box sx={{
                              display: "flex", flexDirection: "column", alignItems: "center",
                              justifyContent: "center", gap: 1.5, py: 5,
                              background: "rgba(255,255,255,0.03)",
                            }}>
                              <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
                                Tu navegador no puede mostrar el PDF aquí.
                              </Typography>
                              <Button
                                component="a" href={cvHref} target="_blank" rel="noopener noreferrer"
                                size="small" variant="outlined"
                                sx={{ borderRadius: "8px", textTransform: "none", color: "#a5b4fc", borderColor: "rgba(99,102,241,0.4)" }}
                              >
                                Abrir en nueva pestaña
                              </Button>
                            </Box>
                          </object>
                        </Box>
                      ) : (
                        <Box sx={{
                          p: 2.5, borderRadius: "12px", textAlign: "center",
                          background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)",
                        }}>
                          <Typography variant="body2" sx={{ color: "text.disabled", lineHeight: 1.7 }}>
                            💡 Los archivos Word no se pueden previsualizar en el navegador.<br />
                            Usa el botón de arriba para descargar y abrir en tu dispositivo.
                          </Typography>
                        </Box>
                      )}
                    </>
                  );
                })() : (
                  <Box sx={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", gap: 1.5, py: 8,
                  }}>
                    <ArticleOutlined sx={{ fontSize: 52, color: "rgba(255,255,255,0.1)" }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "text.disabled" }}>
                      Sin CV adjunto
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.disabled", textAlign: "center", maxWidth: 220 }}>
                      El candidato no subió ningún currículum durante su postulación.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ p: 2.5, borderTop: "1px solid rgba(255,255,255,0.07)", background: "rgba(0,0,0,0.3)", display: "flex", flexDirection: "column", gap: 1.5 }}>
              {saving && <LinearProgress sx={{ borderRadius: 2, mb: 0.5, bgcolor: "rgba(255,255,255,0.06)", "& .MuiLinearProgress-bar": { bgcolor: "#6366f1" } }} />}

              {/* Estado: Rechazado — solo mostrar reactivar */}
              {selected.estado === "rechazada" ? (
                <Button fullWidth variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <Refresh />}
                  onClick={() => cambiarEstado(selected.id, "pendiente")} disabled={saving}
                  sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700,
                    borderColor: "rgba(99,102,241,0.3)", color: "#a5b4fc",
                    "&:hover": { background: "rgba(99,102,241,0.08)", borderColor: "#6366f1" } }}>
                  Reactivar candidato al proceso
                </Button>
              ) : (
                <>
              {selected.estado !== "aprobada" && selected.estado !== "rechazada" && selected.estado !== "en_proceso" && (
                <Button fullWidth variant="contained" startIcon={<CalendarMonth />} onClick={() => setEntrevistaOpen(true)} disabled={saving}
                  sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700,
                    background: "linear-gradient(90deg,#3b82f6,#6366f1)", "&:hover": { background: "linear-gradient(90deg,#2563eb,#4f46e5)" } }}>
                  Agendar entrevista
                </Button>
              )}

              {/* Botón: Continuar con el proceso — solo cuando terminó la entrevista */}
              {selected.estado === "en_entrevista" && (
                <Button fullWidth variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Groups />}
                  onClick={() => cambiarEstado(selected.id, "en_proceso")}
                  disabled={saving}
                  sx={{
                    borderRadius: "12px", textTransform: "none", fontWeight: 700, fontSize: "0.95rem",
                    background: "linear-gradient(90deg,#0891b2,#06b6d4)",
                    boxShadow: "0 4px 20px rgba(6,182,212,0.35)",
                    "&:hover": { background: "linear-gradient(90deg,#0e7490,#0891b2)", boxShadow: "0 6px 28px rgba(6,182,212,0.5)", transform: "translateY(-1px)" },
                    "&:disabled": { background: "rgba(6,182,212,0.2)" },
                    transition: "all 0.2s",
                  }}>
                  Continuar con el proceso
                </Button>
              )}

              <Box sx={{ display: "flex", gap: 1 }}>
                {selected.estado === "pendiente" && (
                  <Button fullWidth size="small" startIcon={<Visibility />}
                    onClick={() => cambiarEstado(selected.id, "en_revision")} disabled={saving}
                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600,
                      background: "rgba(59,130,246,0.1)", color: "#60a5fa", border: "1px solid rgba(59,130,246,0.2)" }}>
                    Iniciar revisión
                  </Button>
                )}
                {selected.estado !== "aprobada" && selected.estado !== "rechazada" && (
                  <Button fullWidth size="small" startIcon={<CheckCircle />}
                    onClick={() => setConfirmOpen({ open: true, accion: "aprobar", id: selected.id })} disabled={saving}
                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600,
                      background: "rgba(34,197,94,0.1)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.2)" }}>
                    Aprobar
                  </Button>
                )}
                {selected.estado !== "rechazada" && (
                  <Button fullWidth size="small" startIcon={<Cancel />}
                    onClick={() => { setRechazoId(selected.id); setRechazoMotivo(""); setRechazoOpen(true); }} disabled={saving}
                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600,
                      background: "rgba(239,68,68,0.08)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" }}>
                    Rechazar
                  </Button>
                )}
              </Box>
                </>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* DIALOG: Agendar entrevista */}
      <Dialog open={entrevistaOpen} onClose={() => setEntrevistaOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", background: "#0f1630", border: "1px solid rgba(59,130,246,0.2)" } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "rgba(59,130,246,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CalendarMonth sx={{ color: "#60a5fa" }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Agendar entrevista</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{selected?.nombre_completo}</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField label="Fecha *" type="date" fullWidth size="small" value={entrevistaForm.fecha}
              onChange={e => setEntrevistaForm(p => ({ ...p, fecha: e.target.value }))}
              InputLabelProps={{ shrink: true }} inputProps={{ min: new Date().toISOString().split("T")[0] }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
            <TextField label="Hora *" type="time" fullWidth size="small" value={entrevistaForm.hora}
              onChange={e => setEntrevistaForm(p => ({ ...p, hora: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          </Box>
          <TextField select label="Tipo de entrevista" fullWidth size="small" value={entrevistaForm.tipo}
            onChange={e => setEntrevistaForm(p => ({ ...p, tipo: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
            <MenuItem value="videollamada">📹 Videollamada</MenuItem>
            <MenuItem value="presencial">🏢 Presencial</MenuItem>
            <MenuItem value="telefonica">📞 Llamada telefónica</MenuItem>
          </TextField>
          <TextField label="Lugar / Liga" fullWidth size="small" value={entrevistaForm.lugar}
            onChange={e => setEntrevistaForm(p => ({ ...p, lugar: e.target.value }))}
            placeholder="meet.google.com/abc-xyz o Av. Principal 123"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField multiline rows={3} label="Notas para el candidato (opcional)" fullWidth size="small"
            value={entrevistaForm.notas} onChange={e => setEntrevistaForm(p => ({ ...p, notas: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <Box sx={{ p: 1.5, borderRadius: "10px", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.15)" }}>
            <Typography variant="caption" sx={{ color: "#60a5fa" }}>
              📧 Se enviará confirmación automática a <strong>{selected?.email}</strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEntrevistaOpen(false)} disabled={saving}
            sx={{ borderRadius: "10px", textTransform: "none", color: "text.secondary" }}>Cancelar</Button>
          <Button variant="contained" onClick={agendarEntrevista} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <CalendarMonth />}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, background: "linear-gradient(90deg,#3b82f6,#6366f1)" }}>
            {saving ? "Enviando…" : "Confirmar y notificar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: Confirmar acción candidato */}
      <Dialog open={confirmOpen.open} onClose={() => setConfirmOpen({ open: false, accion: null, id: null })} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", background: "#0f1630", border: "1px solid rgba(255,255,255,0.08)" } }}>
        <DialogTitle>{confirmOpen.accion === "aprobar" ? "✅ Aprobar postulación" : "❌ Rechazar postulación"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmOpen.accion === "aprobar"
              ? "Se creará un usuario colaborador y se enviará un correo de bienvenida con las credenciales. Esta acción no se puede deshacer."
              : "El candidato no avanzará en el proceso. Puedes reactivar la postulación manualmente después."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmOpen({ open: false, accion: null, id: null })} disabled={saving}
            sx={{ borderRadius: "10px", textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : confirmOpen.accion === "aprobar" ? <CheckCircle /> : <Cancel />}
            onClick={() => confirmOpen.accion === "aprobar" ? aprobarPostulacion(confirmOpen.id) : cambiarEstado(confirmOpen.id, "rechazada")}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700,
              background: confirmOpen.accion === "aprobar" ? "linear-gradient(90deg,#16a34a,#22c55e)" : "#dc2626" }}>
            {saving ? "Procesando…" : confirmOpen.accion === "aprobar" ? "Sí, aprobar" : "Sí, rechazar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: Crear / Editar vacante */}
      <Dialog open={vacanteDialogOpen} onClose={() => setVacanteDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", background: "#0f1630", border: "1px solid rgba(99,102,241,0.2)" } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "rgba(99,102,241,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BusinessCenter sx={{ color: "#a5b4fc" }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{vacanteEditId ? "Editar vacante" : "Nueva vacante"}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField label="Título del puesto *" fullWidth size="small" value={vacanteForm.titulo}
            onChange={e => setVacanteForm(p => ({ ...p, titulo: e.target.value }))}
            placeholder="Ej: Fotógrafo / Camarógrafo"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField label="Área / Departamento" fullWidth size="small" value={vacanteForm.area}
              onChange={e => setVacanteForm(p => ({ ...p, area: e.target.value }))}
              placeholder="Ej: Producción"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
            <TextField select label="Tipo de contrato" fullWidth size="small" value={vacanteForm.tipo}
              onChange={e => setVacanteForm(p => ({ ...p, tipo: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
              <MenuItem value="tiempo_completo">Tiempo completo</MenuItem>
              <MenuItem value="medio_tiempo">Medio tiempo</MenuItem>
              <MenuItem value="freelance">Freelance</MenuItem>
              <MenuItem value="por_evento">Por evento</MenuItem>
            </TextField>
          </Box>
          <TextField multiline rows={3} label="Descripción del puesto" fullWidth size="small" value={vacanteForm.descripcion}
            onChange={e => setVacanteForm(p => ({ ...p, descripcion: e.target.value }))}
            placeholder="¿Qué hará esta persona en el equipo?"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField multiline rows={3} label="Requisitos" fullWidth size="small" value={vacanteForm.requisitos}
            onChange={e => setVacanteForm(p => ({ ...p, requisitos: e.target.value }))}
            placeholder="Experiencia mínima, habilidades requeridas…"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField multiline rows={2} label="Beneficios (opcional)" fullWidth size="small" value={vacanteForm.beneficios}
            onChange={e => setVacanteForm(p => ({ ...p, beneficios: e.target.value }))}
            placeholder="Pagos semanales, acceso a proyectos increíbles…"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
            p: 1.5, borderRadius: "10px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Vacante activa</Typography>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>Visible en el formulario de postulación</Typography>
            </Box>
            <Switch checked={vacanteForm.activo} onChange={e => setVacanteForm(p => ({ ...p, activo: e.target.checked }))}
              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#6366f1" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#6366f1" } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setVacanteDialogOpen(false)} disabled={savingVacante}
            sx={{ borderRadius: "10px", textTransform: "none", color: "text.secondary" }}>Cancelar</Button>
          <Button variant="contained" onClick={guardarVacante} disabled={savingVacante}
            startIcon={savingVacante ? <CircularProgress size={16} /> : vacanteEditId ? <Edit /> : <Add />}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700,
              background: "linear-gradient(90deg,#6366f1,#8b5cf6)", "&:hover": { background: "linear-gradient(90deg,#4f46e5,#7c3aed)" } }}>
            {savingVacante ? "Guardando…" : vacanteEditId ? "Guardar cambios" : "Crear vacante"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: Confirmar eliminar vacante */}
      <Dialog open={!!confirmDeleteVacante} onClose={() => setConfirmDeleteVacante(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", background: "#0f1630", border: "1px solid rgba(239,68,68,0.2)" } }}>
        <DialogTitle>🗑️ Eliminar vacante</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            ¿Eliminar la vacante <strong style={{ color: "#fff" }}>{confirmDeleteVacante?.titulo}</strong>?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDeleteVacante(null)}
            sx={{ borderRadius: "10px", textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" onClick={eliminarVacante}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, background: "#dc2626" }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: Rechazar con retroalimentación */}
      <Dialog open={rechazoOpen} onClose={() => !saving && setRechazoOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", background: "#0f1630", border: "1px solid rgba(239,68,68,0.25)" } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "rgba(239,68,68,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Cancel sx={{ color: "#ef4444" }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Rechazar postulación</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{selected?.nombre_completo}</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            El candidato no avanzará en el proceso de selección. Incluye retroalimentación para que reciba por correo el motivo de la decisión.
          </Typography>
          <TextField
            multiline rows={4}
            label="Retroalimentación para el candidato (opcional)"
            placeholder="Ej: Agradecemos tu interés. En esta ocasión buscamos un perfil con mayor experiencia en producción audiovisual. Te animamos a postularte en futuras convocatorias…"
            fullWidth size="small"
            value={rechazoMotivo}
            onChange={e => setRechazoMotivo(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -1.5 }}>
            <Typography variant="caption" sx={{ color: "text.disabled" }}>{rechazoMotivo.length}/1000</Typography>
          </Box>
          <Box sx={{ p: 1.5, borderRadius: "10px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.18)" }}>
            <Typography variant="caption" sx={{ color: "#fca5a5" }}>
              📧 {rechazoMotivo.trim()
                ? "Se enviará retroalimentación personalizada"
                : "Se enviará un correo estándar de no selección"
              } a <strong>{selected?.email}</strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setRechazoOpen(false)} disabled={saving}
            sx={{ borderRadius: "10px", textTransform: "none", color: "text.secondary" }}>Cancelar</Button>
          <Button variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Cancel />}
            onClick={() => rechazarPostulacion(rechazoId, rechazoMotivo)}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700,
              background: "#dc2626", "&:hover": { background: "#b91c1c" } }}>
            {saving ? "Procesando…" : "Rechazar y notificar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.sev} sx={{ borderRadius: "12px" }} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminRHPanel;
